import { useEffect, type CSSProperties, type ReactNode } from 'react';
import type {
  CommunityLogoStyle,
  CommunityShape,
  CommunityTextBox,
  CommunityZones,
  PortraitZone,
} from '../../types/api';
import {
  backdropLuminance,
  backdropStyle,
  boxStyle,
  canvasUnits,
  textInk,
  textValue,
} from './communityPreviewStyle';
import {
  LIGHT_INK,
  NEUTRAL_PALETTE,
  portraitBox,
  resolveColor,
  upgradeZones,
  type BrandPalette,
} from './communityZones';
import { fontFamilyCss, fontWeightFor, loadCatalogFont } from './fontCatalog';
import { useFontCatalog } from './useFontCatalog';

/**
 * A community design drawn in CSS: backdrop, shapes, portrait and value
 * placeholders, every text box in its own style, and the logo.
 *
 * One component behind three screens — the studio's canvas, the gallery tile
 * and the brief's pin — so a colour or gradient design shows without any image
 * at all, and all three agree on what the design looks like. It is an
 * approximation, as the slide editor's stage is: the fonts and the wrapping are
 * the browser's, the type is shown at its largest rung and not stepped down,
 * and the server's Pillow render stays the truth. A box set in a catalogue
 * font is drawn in the Google Fonts copy of that face, loaded on first use.
 *
 * Every size is in `cqw` against the square stage, so the same fractions and
 * px-on-1080 numbers the renderer reads draw at any size on screen.
 */

const VALIGN_JUSTIFY: Record<CommunityTextBox['valign'], CSSProperties['justifyContent']> = {
  top: 'flex-start',
  middle: 'center',
  bottom: 'flex-end',
};

/** A shape, filling the box it is placed in. */
export function ShapeFill({ shape, palette }: { shape: CommunityShape; palette: BrandPalette }) {
  const fill = resolveColor(shape.fill, palette);
  if (shape.kind === 'line') {
    const horizontal = shape.box.w >= shape.box.h;
    const stroke = `${shape.thickness * 100}cqw`;
    return (
      <span
        className="design-preview__line"
        style={{
          background: fill,
          opacity: shape.opacity,
          ...(horizontal
            ? { left: 0, right: 0, top: '50%', height: stroke, transform: 'translateY(-50%)' }
            : { top: 0, bottom: 0, left: '50%', width: stroke, transform: 'translateX(-50%)' }),
        }}
      />
    );
  }
  return (
    <span
      className="design-preview__shape"
      style={{
        background: fill,
        opacity: shape.opacity,
        borderRadius:
          shape.kind === 'ellipse'
            ? '50%'
            : `${shape.radius * Math.min(shape.box.w, shape.box.h) * 100}cqw`,
      }}
    />
  );
}

export interface TextFillProps {
  text: CommunityTextBox;
  /** What the box says: the member's field, else the author's default. */
  value: string;
  palette: BrandPalette;
  backdropLuma: number;
  /** Shown faintly when the box has nothing to say — the studio's affordance. */
  emptyLabel?: string | null;
}

/**
 * A text box's words in its own style, aligned inside the box it is placed in.
 *
 * A box with `font` reads the session's catalogue (fetched only once some box
 * on screen names a font) and loads that one face. Until the catalogue answers,
 * or if the slug is not in it, the box keeps the theme's face — what the
 * renderer does with a slug it cannot use.
 */
export function TextFill({ text, value, palette, backdropLuma, emptyLabel = null }: TextFillProps) {
  const slug = text.font ?? null;
  const catalog = useFontCatalog(slug !== null);
  const font = slug !== null ? (catalog.bySlug.get(slug) ?? null) : null;
  useEffect(() => {
    if (font) loadCatalogFont(font);
  }, [font]);
  const ink = textInk(text.color, palette, backdropLuma);
  const isEmpty = value.trim().length === 0;
  // Mirror of the renderer: a shadow goes under light ink on a photo.
  const shadow = text.color === 'auto' && ink === LIGHT_INK && backdropLuma <= 0.2;
  return (
    <span
      className="design-preview__text"
      style={{
        justifyContent: VALIGN_JUSTIFY[text.valign],
        textAlign: text.align,
      }}
    >
      <span
        className={
          isEmpty ? 'design-preview__words design-preview__words--empty' : 'design-preview__words'
        }
        style={{
          color: ink,
          fontSize: canvasUnits(text.size_max),
          ...(font ? { fontFamily: fontFamilyCss(font) } : {}),
          fontWeight: font ? fontWeightFor(font, text.weight) : text.weight === 'bold' ? 700 : 400,
          letterSpacing: canvasUnits(text.tracking),
          lineHeight: text.line_spacing,
          textTransform: text.uppercase ? 'uppercase' : 'none',
          WebkitLineClamp: text.max_lines,
          ...(shadow ? { textShadow: '0 0.2cqw 0.6cqw rgba(0, 0, 0, 0.45)' } : {}),
        }}
      >
        {isEmpty ? emptyLabel : value}
      </span>
    </span>
  );
}

/** The logo, contain-fitted into its box; a labelled chip until one is on file. */
export function LogoFill({
  logoUrl,
  style,
}: {
  logoUrl: string | null;
  style: CommunityLogoStyle;
}) {
  return (
    <span
      className={
        style === 'chip'
          ? 'design-preview__logo design-preview__logo--chip'
          : 'design-preview__logo'
      }
    >
      {logoUrl ? (
        <img src={logoUrl} alt="" draggable={false} />
      ) : (
        <span className="design-preview__placeholder">Logo</span>
      )}
    </span>
  );
}

/** The portrait circle's placeholder. */
export function PortraitFill({ children }: { children?: ReactNode }) {
  return <span className="design-preview__portrait">{children ?? 'Photo'}</span>;
}

/** The value callout's placeholder. */
export function ValueFill({
  value,
  palette,
  backdropLuma,
}: {
  value: string | null;
  palette: BrandPalette;
  backdropLuma: number;
}) {
  return (
    <span
      className="design-preview__value"
      style={{ color: textInk('auto', palette, backdropLuma), borderColor: palette.accent }}
    >
      {value && value.trim() ? value : '20% off'}
    </span>
  );
}

export interface CommunityDesignPreviewProps {
  /** Either map version; a v1 map is drawn through its v2 upgrade. */
  zones: CommunityZones;
  /** The backdrop image for an image design, or null. */
  imageUrl: string | null;
  palette?: BrandPalette;
  /** The member's text per box id; missing ones show the author's default. */
  fields?: Record<string, string>;
  /** What an empty headline box shows — the topic or the caption, at review. */
  fallbackHeadline?: string;
  logoUrl?: string | null;
  /** False draws no logo even when the design has one (review's logo toggle). */
  showLogo?: boolean;
  /** The one item row's value, for the value callout. */
  itemValue?: string | null;
  /** Accessible name; the preview is otherwise a picture. */
  label: string;
  className?: string;
}

/** The static preview: the gallery tile, the brief pin, the review stage. */
export function CommunityDesignPreview({
  zones,
  imageUrl,
  palette = NEUTRAL_PALETTE,
  fields,
  fallbackHeadline = 'Your headline',
  logoUrl = null,
  showLogo = true,
  itemValue = null,
  label,
  className,
}: CommunityDesignPreviewProps) {
  const design = upgradeZones(zones);
  const luma = backdropLuminance(design.canvas, palette);
  const portrait: PortraitZone | null = design.portrait;

  return (
    <div
      className={className ? `design-preview ${className}` : 'design-preview'}
      role="img"
      aria-label={label}
    >
      <span
        className="design-preview__backdrop"
        style={backdropStyle(design.canvas, palette, imageUrl)}
      />
      {design.shapes.map((shape) => (
        <span className="design-preview__layer" key={shape.id} style={boxStyle(shape.box)}>
          <ShapeFill shape={shape} palette={palette} />
        </span>
      ))}
      {portrait ? (
        <span className="design-preview__layer" style={boxStyle(portraitBox(portrait))}>
          <PortraitFill />
        </span>
      ) : null}
      {design.value ? (
        <span className="design-preview__layer" style={boxStyle(design.value)}>
          <ValueFill value={itemValue} palette={palette} backdropLuma={luma} />
        </span>
      ) : null}
      {design.texts.map((text) => (
        <span className="design-preview__layer" key={text.id} style={boxStyle(text.box)}>
          <TextFill
            text={text}
            value={textValue(text, fields, fallbackHeadline)}
            palette={palette}
            backdropLuma={luma}
          />
        </span>
      ))}
      {design.logo.enabled && showLogo ? (
        <span className="design-preview__layer" style={boxStyle(design.logo.box)}>
          <LogoFill logoUrl={logoUrl} style={design.logo.style} />
        </span>
      ) : null}
    </div>
  );
}
